import { addDagen, dagSleutel, minuten, tussen, urenTussen, werkdagenLijst } from './datum';
import { reisUren } from './reis';
import type { Afwezigheid, BezetMap, Engineer, Feestdag, ISODate, Instellingen, Taak } from './types';

export type DagKleur = 'ruimte' | 'vol' | 'overvol' | 'afwezig' | 'geenwerkdag';

/**
 * Rekencontext: geïndexeerde gegevens van één organisatie. Alle capaciteitsregels uit SPEC §3.1.
 * Gebruikt in de browser (weekrooster, suggestie) én in de API/worker.
 */
export class Kalender {
  readonly engineers: Engineer[];
  readonly taken: Taak[];
  readonly cfg: Instellingen;
  private engById = new Map<string, Engineer>();
  private heleFeestdagen = new Set<ISODate>();
  private deelFeestdagen = new Map<ISODate, number>();
  private afwPerEng = new Map<string, Afwezigheid[]>();
  private takenPerEng = new Map<string, Taak[]>();
  private bezet: BezetMap;

  constructor(opts: { engineers: Engineer[]; taken: Taak[]; afwezigheden: Afwezigheid[]; feestdagen: Feestdag[]; bezet?: BezetMap; instellingen: Instellingen }) {
    this.engineers = opts.engineers;
    this.taken = opts.taken;
    this.cfg = opts.instellingen;
    this.bezet = opts.bezet ?? new Map();
    for (const e of opts.engineers) this.engById.set(e.id, e);
    for (const f of opts.feestdagen) {
      if (!f.vanTijd) this.heleFeestdagen.add(f.datum);
      else this.deelFeestdagen.set(f.datum, (this.deelFeestdagen.get(f.datum) ?? 0) + urenTussen(f.vanTijd, f.totTijd));
    }
    for (const a of opts.afwezigheden) {
      const l = this.afwPerEng.get(a.engineerId) ?? [];
      l.push(a);
      this.afwPerEng.set(a.engineerId, l);
    }
    for (const t of opts.taken) {
      if (t.status !== 'GEPLAND' || !t.engineerId) continue;
      const l = this.takenPerEng.get(t.engineerId) ?? [];
      l.push(t);
      this.takenPerEng.set(t.engineerId, l);
    }
  }

  engineer(id?: string | null): Engineer | undefined {
    return id ? this.engById.get(id) : undefined;
  }

  isHeleFeestdag(d: ISODate): boolean {
    return this.heleFeestdagen.has(d);
  }

  /** Hele-dag-afwezigheid van de engineer op deze dag. */
  afwezigOp(e: Engineer, d: ISODate): Afwezigheid | undefined {
    return (this.afwPerEng.get(e.id) ?? []).find((a) => !a.vanTijd && tussen(d, a.van, a.tot));
  }

  /** SPEC §3.1 – werkdag; afw=true: verlof/ziek telt als geen werkdag. */
  isWerkdag(e: Engineer, d: ISODate, afw = true): boolean {
    if (this.heleFeestdagen.has(d)) return false;
    if (!werkdagenLijst(e.werkdagen).includes(dagSleutel(d))) return false;
    if (afw && this.afwezigOp(e, d)) return false;
    return true;
  }

  /** Uren die afgaan door gedeeltelijke vrije dagen en gedeeltelijk verlof. */
  deelUren(e: Engineer, d: ISODate): number {
    let u = this.deelFeestdagen.get(d) ?? 0;
    for (const a of this.afwPerEng.get(e.id) ?? []) if (a.vanTijd && tussen(d, a.van, a.tot)) u += urenTussen(a.vanTijd, a.totTijd);
    return u;
  }

  capaciteit(e: Engineer, d: ISODate): number {
    if (!this.isWerkdag(e, d)) return 0;
    return Math.max(0, (e.urenPerDag ?? this.cfg.engUren) - this.deelUren(e, d));
  }

  takenOp(engineerId: string, d: ISODate, uitgezonderd?: string | null): Taak[] {
    return (this.takenPerEng.get(engineerId) ?? []).filter(
      (t) => t.id !== uitgezonderd && t.startdatum && tussen(d, t.startdatum, t.einddatum),
    );
  }

  /** Uren van één taak op één dag (incl. reisuren). */
  takenUrenOpDag(t: Taak, d: ISODate): number {
    const u = d === t.einddatum ? (t.urenLaatsteDag ?? t.urenPerDag ?? 0) : (t.urenPerDag ?? 0);
    const r = t.werkvorm === 'ONSITE' ? reisUren(t.reisMin, this.cfg) : 0;
    return u + r;
  }

  gepland(e: Engineer, d: ISODate, uitgezonderd?: string | null): number {
    if (this.heleFeestdagen.has(d)) return 0;
    return this.takenOp(e.id, d, uitgezonderd).reduce((s, t) => s + this.takenUrenOpDag(t, d), 0);
  }

  /** SPEC §3.1 – bezet in Outlook binnen het venster, exclusief de eigen app-afspraken (incl. reistijd). Max 8. */
  bezetUren(e: Engineer, d: ISODate): number {
    const view = this.bezet.get(e.id)?.get(d);
    if (!view) return 0;
    const start = minuten(this.cfg.outlookVan);
    const eind = minuten(this.cfg.outlookTot);
    const eigen = this.takenOp(e.id, d)
      .filter((t) => t.van)
      .map((t) => {
        const r = t.werkvorm === 'ONSITE' ? (t.reisMin ?? 0) : 0;
        return [minuten(t.van) - r, minuten(t.tot) + r] as const;
      });
    let vakken = 0;
    for (let m = start; m < eind; m += 15) {
      const ch = view[Math.floor(m / 15)];
      if (ch !== '2' && ch !== '3') continue;
      if (eigen.some(([a, b]) => a <= m && b > m)) continue;
      vakken++;
    }
    return Math.min(8, (vakken * 15) / 60);
  }

  vrij(e: Engineer, d: ISODate, uitgezonderd?: string | null): number {
    return this.capaciteit(e, d) - this.gepland(e, d, uitgezonderd) - this.bezetUren(e, d);
  }

  /** SPEC §3.11 – kleur van een dagvak in het weekrooster. */
  dagKleur(e: Engineer, d: ISODate): DagKleur {
    const werk = this.isWerkdag(e, d, false);
    const afw = this.afwezigOp(e, d);
    if (afw) return this.gepland(e, d) > 0 ? 'overvol' : 'afwezig';
    if (!werk) return 'geenwerkdag';
    const v = Math.round(this.vrij(e, d) * 100) / 100;
    if (v < 0) return 'overvol';
    if (v === 0) return 'vol';
    return 'ruimte';
  }

  /** SPEC §3.4 – de dagen waarop een taak van n werkdagen vanaf start valt. */
  planDagen(e: Engineer, start: ISODate, n: number): ISODate[] {
    const dagen: ISODate[] = [start];
    let d = start;
    let guard = 0;
    while (dagen.length < Math.max(1, n) && guard < 400) {
      d = addDagen(d, 1);
      guard++;
      if (this.isWerkdag(e, d)) dagen.push(d);
    }
    return dagen;
  }
}

/** Outlook-herhalingsdagen voor een taak: werkdagen van de engineer + de weekdag van de startdatum. */
export function outlookDagen(e: Engineer, start: ISODate): string[] {
  const namen: Record<string, string> = { ma: 'monday', di: 'tuesday', wo: 'wednesday', do: 'thursday', vr: 'friday', za: 'saturday', zo: 'sunday' };
  const set = new Set(werkdagenLijst(e.werkdagen));
  set.add(dagSleutel(start));
  return Object.keys(namen).filter((k) => set.has(k as never)).map((k) => namen[k]);
}
